<template>
  <!-- XOL-626 (S13/S18): a read-only rate card. Presentational: no fetches, no PostMixins. Each
       parent renders its own heading (and, for the telco, an intro line). Used by RTS step 3's
       telco view (applies-to-you) and by embedded Sharer Finance for a non-manager. It never shows
       the monthly fee: the telco view must not, and the Sharer Finance pane already shows Current
       Fees and the Calculated rate. -->
  <div class="sharer-rate-card">

    <p v-if="!rate" class="sharer-rate-card__empty text-regular">No rate assigned to this sharer.</p>

    <template v-else>
      <p class="sharer-rate-card__summary text-regular">
        <span>Rate <strong>{{ rate.name }}</strong></span>
        <span v-if="version">Version <strong>v{{ version.version_no }}</strong></span>
        <!-- D24: the rate that applies today (this sharer's position, the tier the API computes the
             way the Fee Schedule does), omitted when the API could not resolve a position. The
             ", tier T{t}" part is dropped when there is no current tier. Text, so colour is never
             the only signal. -->
        <span v-if="entryPosition !== null">{{ entryLabel }} <strong>{{ entryTail }}</strong></span>
      </p>

      <template v-if="hasTiers">
        <rate-tiers-cell :row-data="version" hide-drawer-affordance
          :highlight-position="entryPosition" :highlight-tier="highlightTier"
          :applies-label="appliesToYou ? 'your current rate' : 'applies to this sharer'" />

        <div v-if="lgHint" class="sharer-rate-card__lg-hint text-regular">{{ lgHint }}</div>
        <div v-if="positionNote" class="sharer-rate-card__note text-regular">{{ positionNote }}</div>
        <div v-if="tierNote" class="sharer-rate-card__note text-regular">{{ tierNote }}</div>
      </template>

      <p v-else class="sharer-rate-card__empty text-regular">Tier rates are unavailable for this rate.</p>
    </template>

  </div>
</template>

<script>
import RateTiersCell from '@/components/finance/rates/RateTiersCell'
import { bandPositions, displayedPositions, displayedTierSeqs } from '@/lib/rateBand'

// "#6–#7 and #9" / "T6–T9": ascending numbers as ranges, with a literal en-dash (U+2013).
function rangesText (nums, prefix) {
  var parts = []
  var start = null
  var prev = null

  nums.forEach(function (n) {
    if (start === null) {
      start = prev = n
    } else if (n === prev + 1) {
      prev = n
    } else {
      parts.push([start, prev])
      start = prev = n
    }
  })

  if (start !== null) parts.push([start, prev])

  var texts = parts.map(function (r) {
    return r[0] === r[1] ? prefix + r[0] : prefix + r[0] + '–' + prefix + r[1]
  })

  return texts.length > 1 ? texts.slice(0, -1).join(', ') + ' and ' + texts[texts.length - 1] : (texts[0] || '')
}

export default {
  name: 'sharer-rate-card',

  components: {
    RateTiersCell
  },

  props: {
    // { id, name } | null
    rate: {
      type: Object,
      default: null
    },

    // The rate version, in the GET /licensees/action/{id}/rate shape (tiers, fromSharer, toSharer).
    version: {
      type: Object,
      default: null
    },

    // The read's `position` (D18/D24): `applicablePosition` (the column) and `currentTierIndex` (the tier).
    position: {
      type: Object,
      default: null
    },

    // The telco's own view says "your position"; the Tower Co's says "this sharer".
    appliesToYou: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    sortedTiers: function () {
      var tiers = this.version && Array.isArray(this.version.tiers) ? this.version.tiers : []

      return tiers.slice().sort(function (a, b) { return Number(a.seqNo) - Number(b.seqNo) })
    },

    hasTiers: function () {
      return !!this.version && this.sortedTiers.length > 0
    },

    // D24: the position the rate applies at, or null.
    entryPosition: function () {
      var p = this.position && this.position.applicablePosition

      return (p === null || p === undefined || isNaN(Number(p))) ? null : Number(p)
    },

    // D24: the tier that applies today: `position.currentTierIndex`, a tier `seqNo` (not an array
    // index) that the API computes from months since Agreement Start, as the Fee Schedule does.
    // Null when the API has none; then only the column is highlighted.
    currentTier: function () {
      var t = this.position && this.position.currentTierIndex

      return (t === null || t === undefined || isNaN(Number(t))) ? null : Number(t)
    },

    highlightTier: function () {
      return (this.entryPosition !== null && this.hasTiers) ? this.currentTier : null
    },

    entryLabel: function () {
      return this.appliesToYou ? 'Current rate for your position:' : 'Current rate for this sharer: position'
    },

    // Name the tier only when the table has it, so the text never names a tier the grid
    // doesn't mark (the grid then highlights the column alone).
    currentTierInTable: function () {
      var t = this.currentTier

      return t !== null && this.sortedTiers.some(function (row) { return Number(row.seqNo) === t })
    },

    entryTail: function () {
      return '#' + this.entryPosition + (this.currentTierInTable ? ', tier T' + this.currentTier : '')
    },

    // What the grid renders after the highlight swap.
    shown: function () {
      return displayedPositions(this.version, this.entryPosition)
    },

    // Below 1200px the grid shows fewer columns; name the rest.
    lgHint: function () {
      var narrow = this.shown.narrow
      var later = this.shown.wide.filter(function (p) { return narrow.indexOf(p) === -1 })

      if (later.length === 0) return ''

      return (later.length === 1 ? 'Rate ' : 'Rates ') + rangesText(later, '#') + ' shown on wider screens.'
    },

    // In-band positions the grid does not show at all (after the swap) that actually carry a price.
    hiddenPositions: function () {
      var wide = this.shown.wide
      var tiers = this.sortedTiers

      return bandPositions(this.version).filter(function (p) {
        return wide.indexOf(p) === -1 && tiers.some(function (t) {
          var v = Number(t['telco' + p])

          return !isNaN(v) && v !== 0
        })
      })
    },

    positionNote: function () {
      var hidden = this.hiddenPositions

      if (hidden.length === 0) return ''

      return hidden.length === 1
        ? 'Rate #' + hidden[0] + " isn't shown here."
        : 'Rates ' + rangesText(hidden, '#') + " aren't shown here."
    },

    hiddenTiers: function () {
      var shown = displayedTierSeqs(this.sortedTiers, this.highlightTier).shown

      return this.sortedTiers.map(function (t) { return Number(t.seqNo) }).filter(function (s) {
        return shown.indexOf(s) === -1
      })
    },

    tierNote: function () {
      var hidden = this.hiddenTiers

      if (hidden.length === 0) return ''

      return hidden.length === 1
        ? 'Tier T' + hidden[0] + " isn't shown here."
        : 'Tiers ' + rangesText(hidden, 'T') + " aren't shown here."
    }
  }
}
</script>

<style scoped>
.sharer-rate-card__summary {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin: 0 0 8px;
}

.sharer-rate-card__empty {
  margin: 0;
}

.sharer-rate-card__lg-hint {
  margin-top: 4px;
  font-size: 12px;
}

@media (min-width: 1200px) {
  .sharer-rate-card__lg-hint {
    display: none;
  }
}

.sharer-rate-card__note {
  margin-top: 4px;
  font-size: 12px;
}
</style>
